import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { APP_VERSION, BUILD_DATE } from "../version";
import InstallPromptBubble from "../components/InstallPromptBubble";
import { 
  Shuffle, 
  Vote, 
  Users, 
  Shield, 
  Clock, 
  Gift,
  ChevronRight,
  Check,
  Sparkles,
  LogIn,
  Download,
  FileSpreadsheet,
  Mail,
  Gamepad2,
  Palette,
  Lock,
  User,
  Phone
} from "lucide-react";
import ThemeToggle from "../components/ThemeToggle";

// Composant pour les mots qui défilent avec effet rebond
const RotatingWords = () => {
  const words = ["pif", "hasard", "sort", "clic"];
  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % words.length);
    }, 2000);
    return () => clearInterval(interval);
  }, []);

  return (
    <span className="relative inline-flex items-baseline ml-1 w-[60px] sm:w-[90px] h-[1em] overflow-hidden">
      <AnimatePresence mode="wait">
        <motion.span
          key={currentIndex}
          initial={{ y: 30, opacity: 0, scale: 0.8 }}
          animate={{ 
            y: 0, 
            opacity: 1, 
            scale: 1,
            transition: {
              type: "spring",
              stiffness: 400,
              damping: 15,
              mass: 0.8
            }
          }}
          exit={{ 
            y: -30, 
            opacity: 0, 
            scale: 0.8,
            transition: { duration: 0.2 }
          }}
          className="absolute left-0 bottom-0 text-primary font-normal text-sm sm:text-lg"
        >
          {words[currentIndex]}
        </motion.span>
      </AnimatePresence>
    </span>
  );
};

export default function LandingPage() {
  return (
    <div className="min-h-screen bg-background">
      {/* Install Banner - above header */}
      <InstallPromptBubble />
      
      {/* Header */}
      <header className="sticky top-0 left-0 right-0 z-40 glass">
        <div className="max-w-7xl mx-auto px-3 sm:px-6 py-3 sm:py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-1 sm:gap-2">
            <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-xl bg-primary flex items-center justify-center flex-shrink-0">
              <Shuffle className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
            </div>
            <span className="text-lg sm:text-2xl font-bold text-foreground flex items-baseline" style={{ fontFamily: 'Outfit' }}>
              TIREO<RotatingWords />
            </span>
          </Link>
          
          <nav className="hidden md:flex items-center gap-8">
            <a href="#features" className="text-foreground/70 hover:text-foreground transition-colors font-medium">Fonctionnalités</a>
            <a href="#pricing" className="text-foreground/70 hover:text-foreground transition-colors font-medium">Tarifs</a>
            <Link to="/contact" className="text-foreground/70 hover:text-foreground transition-colors font-medium">Contact</Link>
          </nav>
          
          <div className="flex items-center gap-1 sm:gap-3">
            <ThemeToggle />
            <Link to="/login">
              <Button variant="ghost" className="rounded-full h-8 sm:h-10 text-xs sm:text-sm px-3 sm:px-4" data-testid="header-login-btn">
                <User className="w-4 h-4 mr-1 sm:mr-2" />
                <span>Connexion</span>
              </Button>
            </Link>
            <Link to="/register">
              <Button className="rounded-full bg-primary hover:bg-primary/90 text-xs sm:text-base px-2 sm:px-4 h-8 sm:h-10" data-testid="header-register-btn">
                <span className="hidden sm:inline">Commencer gratuitement</span>
                <span className="sm:hidden">S'inscrire</span>
              </Button>
            </Link>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 px-6 min-h-[90vh] overflow-hidden">
        {/* Video Background */}
        <div className="absolute inset-0 z-0">
          <video
            autoPlay
            loop
            muted
            playsInline
            className="w-full h-full object-cover"
          >
            <source src="https://customer-assets.emergentagent.com/job_1ef17774-2e33-4486-a25e-2779132b3d0d/artifacts/4125glw0_TIREO.mp4" type="video/mp4" />
          </video>
          {/* Smooth gradient overlay - from solid on left to transparent on right */}
          <div className="absolute inset-0 bg-gradient-to-r from-background via-background/90 via-40% to-transparent"></div>
          {/* Bottom fade to next section */}
          <div className="absolute inset-0 bg-gradient-to-t from-background via-transparent to-transparent"></div>
        </div>
        
        <div className="max-w-7xl mx-auto relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center min-h-[60vh]">
            <div className="space-y-8 animate-fade-in-up">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 text-primary text-sm font-medium">
                <Sparkles className="w-4 h-4" />
                <span>Premier tirage offert</span>
              </div>
              
              <h1 className="text-4xl md:text-6xl font-bold tracking-tight text-foreground" style={{ fontFamily: 'Outfit' }}>
                Tirages au sort <br />
                <span className="gradient-text">professionnels</span> et <br />
                sondages <span className="gradient-text">en direct</span>
              </h1>
              
              <p className="text-lg text-muted-foreground max-w-lg leading-relaxed">
                Organisez vos tirages au sort et sondages en toute transparence. 
                Page publique en temps réel, animations spectaculaires, horodatage certifié.
              </p>
              
              <div className="flex flex-wrap gap-4">
                <Link to="/register">
                  <Button size="lg" className="rounded-full bg-primary hover:bg-primary/90 px-8 h-12 text-base" data-testid="hero-cta-btn">
                    Créer mon premier tirage
                    <ChevronRight className="w-5 h-5 ml-2" />
                  </Button>
                </Link>
                <a href="#features">
                  <Button size="lg" variant="outline" className="rounded-full px-8 h-12 text-base">
                    Découvrir
                  </Button>
                </a>
              </div>
              
              <div className="flex items-center gap-6 pt-4">
                <div className="flex items-center gap-2">
                  <Check className="w-5 h-5 text-green-500" />
                  <span className="text-sm text-muted-foreground">Sans engagement</span>
                </div>
                <div className="flex items-center gap-2">
                  <Check className="w-5 h-5 text-green-500" />
                  <span className="text-sm text-muted-foreground">Paiement sécurisé</span>
                </div>
              </div>
            </div>
            
            {/* Right column - video shows through */}
            <div className="hidden lg:block"></div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-20 px-6 bg-muted/50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16 animate-fade-in-up">
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4" style={{ fontFamily: 'Outfit' }}>
              Tout ce qu'il vous faut
            </h2>
            <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
              TIREO vous offre tous les outils pour organiser des tirages au sort et sondages professionnels.
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              {
                icon: Shuffle,
                title: "Tirages au sort",
                description: "Tirage aléatoire avec animation spectaculaire, horodatage certifié et page live."
              },
              {
                icon: Vote,
                title: "Sondages & Votes",
                description: "Créez des sondages avec photos, vidéos YouTube et limite de votes par participant."
              },
              {
                icon: Users,
                title: "Gestion de listes",
                description: "Importez, gérez et exportez vos participants avec champs personnalisés illimités."
              },
              {
                icon: FileSpreadsheet,
                title: "Export marketing",
                description: "Exportez vos contacts avec consentement RGPD pour vos campagnes email et SMS."
              },
              {
                icon: Palette,
                title: "Personnalisation",
                description: "Personnalisez vos pages avec votre logo, couleurs et design unique."
              },
              {
                icon: Mail,
                title: "Emails automatiques",
                description: "Configuration SMTP personnalisée, modèles d'emails et historique des envois."
              },
              {
                icon: Gift,
                title: "Gestion des lots",
                description: "Définissez vos lots et attribuez-les automatiquement aux gagnants."
              },
              {
                icon: Clock,
                title: "Page live",
                description: "Page publique en temps réel avec animations et résultats instantanés."
              },
              {
                icon: Shield,
                title: "RGPD & Conformité",
                description: "Consentement marketing intégré, demande de suppression et transparence totale."
              }
            ].map((feature, index) => (
              <Card 
                key={index} 
                className="bg-card border-border hover:border-primary/30 transition-colors card-hover animate-fade-in-up"
                style={{ animationDelay: `${index * 100}ms` }}
              >
                <CardContent className="p-6">
                  <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center mb-4">
                    <feature.icon className="w-6 h-6 text-primary" />
                  </div>
                  <h3 className="text-xl font-semibold text-foreground mb-2" style={{ fontFamily: 'Outfit' }}>
                    {feature.title}
                  </h3>
                  <p className="text-muted-foreground">
                    {feature.description}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="py-20 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16 animate-fade-in-up">
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4" style={{ fontFamily: 'Outfit' }}>
              Tarifs transparents
            </h2>
            <p className="text-muted-foreground text-lg">
              Choisissez la formule adaptée à vos besoins. <strong>10 jours d'essai gratuit</strong> pour découvrir TIREO.
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto">
            {/* Single Operation */}
            <Card className="bg-card border-border animate-fade-in-up">
              <CardContent className="p-6">
                <div className="w-12 h-12 rounded-xl bg-muted flex items-center justify-center mb-4">
                  <Gift className="w-6 h-6 text-muted-foreground" />
                </div>
                <h3 className="text-xl font-bold text-foreground mb-1" style={{ fontFamily: 'Outfit' }}>
                  À l'unité
                </h3>
                <p className="text-sm text-muted-foreground mb-4">Idéal pour un usage ponctuel</p>
                <div className="mb-4">
                  <span className="text-3xl font-bold text-foreground">9,90€</span>
                  <span className="text-muted-foreground text-sm"> TTC</span>
                </div>
                <p className="text-xs text-muted-foreground mb-4">soit 8,25€ HT / opération</p>
                <ul className="space-y-2 mb-6 text-sm">
                  {["1 tirage OU 1 sondage", "Participants illimités", "Page publique live"].map((item, i) => (
                    <li key={i} className="flex items-center gap-2 text-muted-foreground">
                      <Check className="w-4 h-4 text-green-500 flex-shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>
                <Link to="/register" className="block">
                  <Button variant="outline" className="w-full rounded-full" data-testid="pricing-single-btn">
                    Essayer
                  </Button>
                </Link>
              </CardContent>
            </Card>
            
            {/* Monthly */}
            <Card className="bg-card border-border animate-fade-in-up animation-delay-100">
              <CardContent className="p-6">
                <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center mb-4">
                  <Clock className="w-6 h-6 text-primary" />
                </div>
                <h3 className="text-xl font-bold text-foreground mb-1" style={{ fontFamily: 'Outfit' }}>
                  Mensuel
                </h3>
                <p className="text-sm text-muted-foreground mb-4">Sans engagement</p>
                <div className="mb-4">
                  <span className="text-3xl font-bold text-foreground">29,90€</span>
                  <span className="text-muted-foreground text-sm"> /mois</span>
                </div>
                <p className="text-xs text-muted-foreground mb-4">soit 24,92€ HT</p>
                <ul className="space-y-2 mb-6 text-sm">
                  {["Tirages illimités", "Sondages illimités", "Support prioritaire", "Annulable à tout moment"].map((item, i) => (
                    <li key={i} className="flex items-center gap-2 text-muted-foreground">
                      <Check className="w-4 h-4 text-green-500 flex-shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>
                <Link to="/register" className="block">
                  <Button variant="outline" className="w-full rounded-full" data-testid="pricing-monthly-btn">
                    S'abonner
                  </Button>
                </Link>
              </CardContent>
            </Card>
            
            {/* Annual - BEST VALUE */}
            <Card className="bg-card border-2 border-primary relative overflow-hidden animate-fade-in-up animation-delay-200">
              <div className="absolute top-0 right-0 bg-primary text-white px-3 py-1 text-xs font-bold rounded-bl-xl">
                5 MOIS OFFERTS
              </div>
              <CardContent className="p-6">
                <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center mb-4">
                  <Sparkles className="w-6 h-6 text-primary" />
                </div>
                <h3 className="text-xl font-bold text-foreground mb-1" style={{ fontFamily: 'Outfit' }}>
                  Annuel
                </h3>
                <p className="text-sm text-primary font-medium mb-4">Le meilleur rapport qualité/prix</p>
                <div className="mb-2">
                  <span className="text-3xl font-bold text-foreground">209€</span>
                  <span className="text-muted-foreground text-sm"> /an</span>
                </div>
                <p className="text-xs text-muted-foreground mb-1">soit 174,17€ HT</p>
                <div className="p-2 rounded-lg bg-green-50 dark:bg-green-950 border border-green-200 dark:border-green-800 mb-4">
                  <p className="text-xs text-green-700 dark:text-green-300 font-medium text-center">
                    Payez 7 mois, profitez 12 mois !
                  </p>
                  <p className="text-xs text-green-600 dark:text-green-400 text-center">
                    Économie de 149,50€
                  </p>
                </div>
                <ul className="space-y-2 mb-6 text-sm">
                  {["Tirages illimités", "Sondages illimités", "Support prioritaire"].map((item, i) => (
                    <li key={i} className="flex items-center gap-2 text-muted-foreground">
                      <Check className="w-4 h-4 text-green-500 flex-shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>
                <Link to="/register" className="block">
                  <Button className="w-full rounded-full bg-primary hover:bg-primary/90" data-testid="pricing-annual-btn">
                    Choisir l'annuel
                  </Button>
                </Link>
              </CardContent>
            </Card>
            
            {/* Premium */}
            <Card className="bg-gradient-to-br from-violet-500/10 to-fuchsia-500/10 border-violet-300 dark:border-violet-700 animate-fade-in-up animation-delay-300">
              <CardContent className="p-6">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-violet-500 to-fuchsia-500 flex items-center justify-center mb-4">
                  <Gamepad2 className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-xl font-bold text-foreground mb-1" style={{ fontFamily: 'Outfit' }}>
                  Premium
                </h3>
                <p className="text-sm text-violet-600 dark:text-violet-400 font-medium mb-4">Tout + Jeux interactifs</p>
                <div className="mb-4">
                  <span className="text-3xl font-bold text-foreground">299€</span>
                  <span className="text-muted-foreground text-sm"> /an</span>
                </div>
                <p className="text-xs text-muted-foreground mb-4">soit 249,17€ HT</p>
                <ul className="space-y-2 mb-6 text-sm">
                  {["Tout de l'Annuel", "Roue de la fortune", "Cartes à gratter", "Jeux personnalisés"].map((item, i) => (
                    <li key={i} className="flex items-center gap-2 text-muted-foreground">
                      <Check className="w-4 h-4 text-violet-500 flex-shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>
                <Link to="/register" className="block">
                  <Button className="w-full rounded-full bg-gradient-to-r from-violet-500 to-fuchsia-500 hover:from-violet-600 hover:to-fuchsia-600 text-white" data-testid="pricing-premium-btn">
                    Passer Premium
                  </Button>
                </Link>
              </CardContent>
            </Card>
          </div>
          
          {/* Promo code info */}
          <div className="text-center mt-10">
            <p className="text-muted-foreground">
              Vous avez un <span className="font-medium text-foreground">code promo</span> ? 
              Appliquez-le lors du paiement pour bénéficier de votre réduction.
            </p>
          </div>
        </div>
      </section>

      {/* RGPD Section */}
      <section id="rgpd" className="py-16 px-6 bg-muted/30">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-10">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-green-100 dark:bg-green-900 mb-4">
              <Lock className="w-8 h-8 text-green-600 dark:text-green-400" />
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4" style={{ fontFamily: 'Outfit' }}>
              Vos données sont protégées
            </h2>
            <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
              TIREO s'engage à respecter le RGPD et à protéger les données de vos participants.
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-6">
            <Card className="bg-card border-green-200 dark:border-green-800">
              <CardContent className="p-6 text-center">
                <div className="w-12 h-12 rounded-full bg-green-100 dark:bg-green-900 flex items-center justify-center mx-auto mb-4">
                  <Check className="w-6 h-6 text-green-600" />
                </div>
                <h3 className="font-semibold text-foreground mb-2">Consentement explicite</h3>
                <p className="text-sm text-muted-foreground">
                  Les participants choisissent s'ils acceptent de recevoir des communications marketing.
                </p>
              </CardContent>
            </Card>
            
            <Card className="bg-card border-green-200 dark:border-green-800">
              <CardContent className="p-6 text-center">
                <div className="w-12 h-12 rounded-full bg-green-100 dark:bg-green-900 flex items-center justify-center mx-auto mb-4">
                  <Shield className="w-6 h-6 text-green-600" />
                </div>
                <h3 className="font-semibold text-foreground mb-2">Droit à l'effacement</h3>
                <p className="text-sm text-muted-foreground">
                  Demandez la suppression de vos données à tout moment via <a href="mailto:rgpd@tireo.app" className="text-primary underline">rgpd@tireo.app</a>
                </p>
              </CardContent>
            </Card>
            
            <Card className="bg-card border-green-200 dark:border-green-800">
              <CardContent className="p-6 text-center">
                <div className="w-12 h-12 rounded-full bg-green-100 dark:bg-green-900 flex items-center justify-center mx-auto mb-4">
                  <Download className="w-6 h-6 text-green-600" />
                </div>
                <h3 className="font-semibold text-foreground mb-2">Export conforme</h3>
                <p className="text-sm text-muted-foreground">
                  Exportez uniquement les contacts avec consentement pour vos campagnes marketing.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-6 bg-primary">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6" style={{ fontFamily: 'Outfit' }}>
            Prêt à organiser votre tirage ?
          </h2>
          <p className="text-white/80 text-lg mb-8 max-w-2xl mx-auto">
            Rejoignez les entreprises et associations qui font confiance à TIREO pour leurs tirages au sort et sondages.
          </p>
          <Link to="/register">
            <Button size="lg" className="rounded-full bg-white text-primary hover:bg-white/90 px-8 h-12 text-base" data-testid="cta-register-btn">
              Créer mon compte gratuitement
              <ChevronRight className="w-5 h-5 ml-2" />
            </Button>
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer id="contact" className="py-12 px-6 bg-slate-900 dark:bg-slate-950 text-slate-300">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-4 gap-8">
            <div>
              <div className="flex items-center gap-2 mb-4">
                <div className="w-8 h-8 rounded-lg bg-primary flex items-center justify-center">
                  <Shuffle className="w-4 h-4 text-white" />
                </div>
                <span className="text-xl font-bold text-white" style={{ fontFamily: 'Outfit' }}>TIREO</span>
              </div>
              <p className="text-sm text-slate-400">
                La solution professionnelle pour vos tirages au sort et sondages.
              </p>
            </div>
            
            <div>
              <h4 className="font-semibold text-white mb-4">Produit</h4>
              <ul className="space-y-2 text-sm">
                <li><a href="#features" className="text-slate-400 hover:text-white transition-colors">Fonctionnalités</a></li>
                <li><a href="#pricing" className="text-slate-400 hover:text-white transition-colors">Tarifs</a></li>
              </ul>
            </div>
            
            <div>
              <h4 className="font-semibold text-white mb-4">Légal</h4>
              <ul className="space-y-2 text-sm">
                <li><a href="#" className="text-slate-400 hover:text-white transition-colors">CGU</a></li>
                <li><a href="#" className="text-slate-400 hover:text-white transition-colors">Confidentialité</a></li>
                <li><a href="#rgpd" className="text-slate-400 hover:text-white transition-colors">RGPD</a></li>
              </ul>
            </div>
            
            <div>
              <h4 className="font-semibold text-white mb-4">Contact</h4>
              <ul className="space-y-2 text-sm">
                <li className="text-slate-400">
                  Un service de l'Agence MSC
                </li>
                <li>
                  <a href="tel:0484492700" className="text-slate-400 hover:text-white transition-colors flex items-center gap-2">
                    <Phone className="w-4 h-4" />
                    04 84 49 27 00
                  </a>
                </li>
                <li>
                  <a href="mailto:contact@tireo.app" className="text-slate-400 hover:text-white transition-colors flex items-center gap-2">
                    <Mail className="w-4 h-4" />
                    contact@tireo.app
                  </a>
                </li>
                <li className="pt-2">
                  <Link to="/contact" className="text-blue-400 hover:text-blue-300 transition-colors font-medium">
                    Formulaire de contact →
                  </Link>
                </li>
              </ul>
            </div>
          </div>
          
          <div className="border-t border-slate-700 mt-12 pt-8 text-sm text-center text-slate-500">
            <div className="flex items-center justify-center gap-4">
              <span>© {new Date().getFullYear()} TIREO - Un service de l'Agence MSC. Tous droits réservés.</span>
              <Link to="/admin/login" className="text-slate-600 hover:text-slate-400 transition-colors" title="Administration">
                <Shield className="w-4 h-4" />
              </Link>
            </div>
            <p className="text-[10px] text-slate-600 mt-2">
              v{APP_VERSION} • Déployé le {BUILD_DATE}
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
